// src/autopilotMode/components/ModelCardUploadProgress.js
// Enhanced model card upload progress with branded messaging and smooth transitions

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Text,
  Progress,
  Stack,
  Group,
  Button,
  Card,
  List,
  Transition,
  Center,
  Title
} from '@mantine/core';
import { 
  IconUpload, 
  IconSearch, 
  IconCheck, 
  IconX,
  IconClock,
  IconBolt
} from '@tabler/icons-react';
import AIGeneratingIcon from '../../components/AIGeneratingIcon';

const UPLOAD_STEPS = [
  {
    key: 'upload',
    title: 'Securing Your Document',
    message: 'Uploading your model card safely...',
    icon: IconUpload,
    progressRange: [0, 50]
  },
  {
    key: 'processing', 
    title: 'Making Document Searchable',
    message: 'Creating smart indexes for fast retrieval...',
    icon: IconSearch,
    progressRange: [50, 60]
  },
  {
    key: 'extraction',
    title: 'Extracting Key Insights', 
    message: 'Reading your model card to understand context...',
    icon: 'ai', // Special case for AI icon
    progressRange: [60, 90]
  },
  {
    key: 'complete',
    title: 'Preparing Assessment Context',
    message: 'Setting up your personalized risk assessment...',
    icon: IconBolt,
    progressRange: [90, 100]
  }
];

const EXTRACTION_TARGETS = [
  'Model purpose & use cases',
  'Technical specifications',
  'Training data & methodology', 
  'Performance metrics',
  'Deployment context'
];

function StepIcon({ step, currentStep, isCompleted, size = 20 }) {
  if (isCompleted) {
    return <IconCheck size={size} color="green" />;
  }
  
  if (step.key === currentStep) {
    if (step.icon === 'ai') {
      return <AIGeneratingIcon isGenerating size={size} />;
    }
    const Icon = step.icon;
    return <Icon size={size} color="blue" />;
  }
  
  return <IconClock size={size} color="gray" />;
}

function StepItem({ step, currentStep, completedSteps, progress }) {
  const isActive = step.key === currentStep;
  const isCompleted = completedSteps.includes(step.key);
  const [minProgress, maxProgress] = step.progressRange;
  const isInRange = progress >= minProgress && progress <= maxProgress;

  return (
    <Group spacing="md" align="center">
      <StepIcon 
        step={step} 
        currentStep={currentStep} 
        isCompleted={isCompleted}
      />
      <Box style={{ flex: 1 }}>
        <Text 
          weight={isActive ? 600 : 400} 
          color={isCompleted ? 'green' : isActive ? 'blue' : 'dimmed'}
          size="sm"
        >
          {isCompleted ? '✅' : isInRange ? '🔄' : '⏳'} {step.title}
        </Text>
        {isActive && (
          <Transition mounted={true} transition="fade" duration={300}>
            {(styles) => (
              <Text size="xs" color="dimmed" style={styles}>
                {step.message}
              </Text>
            )}
          </Transition>
        )}
      </Box>
    </Group>
  );
}

export default function ModelCardUploadProgress({ 
  opened, 
  onClose, 
  fileName,
  stage = 'upload',
  progress = 0,
  message = '',
  error = null,
  onCancel
}) {
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState('upload');

  // Update current step based on stage and progress
  useEffect(() => {
    const newCompletedSteps = [];
    let newCurrentStep = 'upload';

    for (const step of UPLOAD_STEPS) {
      const [minProgress, maxProgress] = step.progressRange;
      
      if (progress > maxProgress) {
        newCompletedSteps.push(step.key);
      } else if (progress >= minProgress && progress <= maxProgress) {
        newCurrentStep = step.key;
        break;
      }
    }

    setCompletedSteps(newCompletedSteps);
    setCurrentStep(newCurrentStep);
  }, [stage, progress]);

  const getTimeEstimate = () => {
    if (progress < 50) return '30-60 seconds';
    if (progress < 90) return '20-40 seconds';
    return '5-10 seconds';
  };

  const isComplete = stage === 'complete' && progress === 100;
  const hasError = stage === 'error' || error;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group spacing="sm">
          <Text size="lg" weight={600}>
            🤖 Analyzing Your Model Card
          </Text>
        </Group>
      }
      size="lg"
      centered
      closeOnClickOutside={false}
      closeOnEscape={!hasError}
      withCloseButton={hasError || isComplete}
    >
      <Stack spacing="lg">
        {/* Progress Bar */}
        <Box>
          <Progress 
            value={progress} 
            size="lg" 
            radius="md"
            animate={!hasError && !isComplete}
            color={hasError ? 'red' : isComplete ? 'green' : 'blue'}
          />
          <Group position="apart" mt="xs">
            <Text size="sm" color="dimmed">
              {hasError ? 'Failed' : isComplete ? 'Complete' : `${Math.round(progress)}%`}
            </Text>
            {!hasError && !isComplete && (
              <Text size="sm" color="dimmed">
                Estimated time: {getTimeEstimate()}
              </Text>
            )}
          </Group>
        </Box>

        {/* Error State */}
        {hasError && (
          <Card withBorder radius="md" p="md" bg="red.0">
            <Group spacing="sm">
              <IconX color="red" />
              <Box style={{ flex: 1 }}>
                <Text weight={600} color="red">Upload Failed</Text>
                <Text size="sm" color="dimmed">
                  {error || message || 'An error occurred while processing your model card.'}
                </Text>
              </Box>
            </Group>
          </Card>
        )}

        {/* Step Progress */}
        {!hasError && (
          <Stack spacing="sm">
            {UPLOAD_STEPS.map((step) => (
              <StepItem
                key={step.key}
                step={step}
                currentStep={currentStep}
                completedSteps={completedSteps}
                progress={progress}
              />
            ))}
          </Stack>
        )}

        {/* Information Panel */}
        {!hasError && !isComplete && currentStep === 'extraction' && (
          <Transition mounted={true} transition="slide-up" duration={400}>
            {(styles) => (
              <Card withBorder radius="md" p="md" bg="blue.0" style={styles}>
                <Text weight={600} size="sm" mb="xs">
                  What Huginn is looking for:
                </Text>
                <List size="sm" spacing="xs">
                  {EXTRACTION_TARGETS.map((target, index) => (
                    <List.Item key={index}>
                      <Text size="sm">{target}</Text>
                    </List.Item>
                  ))}
                </List>
              </Card>
            )}
          </Transition>
        )}

        {/* Success State */}
        {isComplete && (
          <Transition mounted={true} transition="slide-up" duration={400}>
            {(styles) => (
              <Card withBorder radius="md" p="md" bg="green.0" style={styles}>
                <Center>
                  <Stack align="center" spacing="sm">
                    <IconCheck size={48} color="green" />
                    <Title order={4} color="green">Analysis Complete!</Title>
                    <Text size="sm" color="dimmed" align="center">
                      Your model card has been successfully processed and is ready for risk assessment.
                    </Text>
                  </Stack>
                </Center>
              </Card>
            )}
          </Transition>
        )}

        {/* Actions */}
        <Group position={hasError ? 'apart' : 'center'}>
          {hasError && (
            <Button variant="outline" onClick={onCancel}>
              Try Again
            </Button>
          )}
          {(hasError || isComplete) && (
            <Button 
              variant={isComplete ? 'filled' : 'outline'}
              onClick={onClose}
            >
              {isComplete ? 'Continue' : 'Close'}
            </Button>
          )}
          {!hasError && !isComplete && onCancel && (
            <Button variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}